import classNames from 'classnames';
// import PropTypes from 'prop-types';
import React, { ReactElement, forwardRef, useRef, useEffect, useCallback, useImperativeHandle, useMemo } from 'react';
import {WebUI, getNid} from "../../wui-core/src/index"
import TimelineItem from './TimelineItem';
import TimelineItemGroup from './TimelineItemGroup';
import {prefix} from "../../wui-core/src/index";
import {TimelineProps} from './iTimeline'
import { WithConfigConsumer } from '../../wui-provider/src/context';

/* const propTypes = {
    pending: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.node
    ]),
    fieldid: PropTypes.string
} */

const defaultProps = {
    fieldid: undefined
}

const Timeline = forwardRef<HTMLUListElement, TimelineProps>((props, ref) => {
    const timelineRef = useRef<HTMLUListElement>(null);
    const hasLabelRef = useRef<boolean>(false); // 是否有label标签
    const hadLabelRef = useRef<boolean>(false); // 是否存在过label标签
    const labelElementsRef = useRef<HTMLDivElement[]>([]); // 缓存label元素
    const lastComputedWidthRef = useRef<string>(''); // 缓存计算结果
    const lastChildrenCountRef = useRef<number>(0); // 缓存children数量

    const {clsPrefix, children, pending, className, mode, reverse, pendingDot, labelWidth, style, dir, ...others} = props;

    // 获取或更新label元素缓存
    const getLabelElements = useCallback(() => {
        const ulDom = timelineRef.current;
        if (!ulDom) return [];

        const currentChildrenCount = React.Children.count(children);

        // 只有在以下情况才重新查询DOM：
        // 1. 缓存为空
        // 2. children数量发生变化
        // 3. 强制更新（mount时）
        if (
            labelElementsRef.current.length === 0 ||
            currentChildrenCount !== lastChildrenCountRef.current
        ) {
            const labelList = ulDom.querySelectorAll<HTMLDivElement>(`.${prefix}-timeline-item-label`);
            labelElementsRef.current = Array.from(labelList);
            lastChildrenCountRef.current = currentChildrenCount;
        }

        return labelElementsRef.current;
    }, [children]);

    const setLabelWidth = useCallback((flag: string) => {
        if (hasLabelRef.current && typeof labelWidth !== 'number') { // 有label标签，并且没有设置label宽度
            const ulDom = timelineRef.current;
            const labelList = getLabelElements(); // 使用缓存的元素

            if (mode === 'alternate') {
                labelList.forEach(dom => {
                    dom.style.removeProperty('width');
                })
                ulDom?.style.removeProperty('margin-left');
                ulDom?.style.removeProperty('margin-right');
                return;
            }

            let maxWidth = 0;
            let paddingWidth = 2;
            labelList.forEach(dom => {
                if (flag === 'update') {
                    dom.style.removeProperty('width');
                }
                let domWidth = dom.offsetWidth;
                maxWidth = maxWidth > domWidth ? maxWidth : domWidth;
            })

            let [min = 92, max = 180] = Array.isArray(labelWidth) ? labelWidth : [];
            if (min > max) {
                [min, max] = [max, min]
            }
            let labelCalcWidth = maxWidth + paddingWidth;
            if (labelCalcWidth < min) {
                labelCalcWidth = min;
            }
            if (labelCalcWidth > max) {
                labelCalcWidth = max;
            }
            let labelDomWidth = labelCalcWidth + 'px';

            // 如果计算结果相同，避免重复应用样式
            if (lastComputedWidthRef.current === labelDomWidth && flag !== 'mount') {
                return;
            }
            lastComputedWidthRef.current = labelDomWidth;

            labelList.forEach(dom => {
                dom.style.setProperty('width', labelDomWidth);
            })
            if (mode === 'right') {
                ulDom?.style.removeProperty('margin-left')
                ulDom?.style.setProperty(dir === 'rtl' ? 'margin-left' : 'margin-right', labelDomWidth)
            } else {
                ulDom?.style.removeProperty('margin-right')
                ulDom?.style.setProperty(dir === 'rtl' ? 'margin-right' : 'margin-left', labelDomWidth)
            }
        } else if (hadLabelRef.current && typeof labelWidth !== 'number') {
            const ulDom = timelineRef.current;
            ulDom?.style.removeProperty('margin-left');
            ulDom?.style.removeProperty('margin-right');
        }
    }, [labelWidth, mode, dir, getLabelElements]);

    useEffect(() => {
        setLabelWidth('mount');
    }, []);

    useEffect(() => {
        // 当这些依赖项变化时，清空缓存并重新计算
        labelElementsRef.current = [];
        lastComputedWidthRef.current = '';
        lastChildrenCountRef.current = 0;
        setLabelWidth('update');
    }, [setLabelWidth, children, labelWidth, mode, reverse]);

    const isGroup = useCallback((ele: ReactElement) => {
        return ele.props.clsPrefix === TimelineItemGroup.defaultProps.clsPrefix
    }, []);

    const itemToArr = useCallback((ele: ReactElement) => {
        let child = ele.props.children;
        if (!child || typeof child === 'string') return [];
        if (React.isValidElement(child)) {
            child = [child]
        }
        return child;
    }, []);

    const flat = useCallback((a: Array<ReactElement>) => { // 把timelineGroup数组扁平化
        let c = a.slice(0); // 操作副本
        for (let i = 0; i < c.length; i++) {
            if (isGroup(c[i])) {
                let child = itemToArr(c[i]);
                c.splice(i, 1, ...child);
                i += child.length - 1;
            }
        }
        return c;
    }, []); // 移除依赖，因为isGroup和itemToArr永远不会变化

    const getPositionCls = useCallback((ele: ReactElement<any>, idx: number) => { // 设置item 类名, group组件在自己组件内部设置（reverse需要额外处理）
        if (isGroup(ele)) return '';
        if (mode) {
            if (mode === 'alternate') {
                return idx % 2 === 0 ? `${clsPrefix}-item-left` : `${clsPrefix}-item-right`;
            } else {
                return `${clsPrefix}-item-${mode}`
            }
        }
        return '';
    }, [mode, clsPrefix]); // 移除isGroup依赖，因为它不会变化

    // 使用useImperativeHandle暴露DOM元素
    useImperativeHandle(ref, () => {
        const node = timelineRef.current;
        if (!node) return null as any;
        // 直接返回DOM节点，这样可以保留所有原生方法和属性
        return node;
    }, []);

    // 使用useMemo缓存children处理结果
    const processedChildren = useMemo(() => {
        const pendingNode = typeof pending === 'boolean' ? null : pending;
        const pendingItem = (pending) ? (
            <TimelineItem pending={!!pending} dot={pendingDot}>{pendingNode}</TimelineItem>
        ) : null;

        const childArr = React.Children.toArray(children);
        const timeLineItems = childArr.filter((item): item is ReactElement =>
            React.isValidElement(item)
        );
        const flatElement = flat(timeLineItems); // 节点扁平化

        if (pendingItem) { // 如果存在pending节点
            flatElement.push(pendingItem as ReactElement);
            timeLineItems.push(pendingItem as ReactElement);
        }

        // 创建副本用于reverse，避免修改原数组
        const finalTimeLineItems = reverse ? [...timeLineItems].reverse() : timeLineItems;
        const hasLabelItem = flatElement.some((item: React.ReactElement<any>) => !!item?.props?.label);

        return {
            timeLineItems: finalTimeLineItems,
            flatElement,
            hasLabelItem
        };
    }, [children, pending, pendingDot, reverse]); // 移除flat依赖，因为它的引用永远不变

    const { timeLineItems, flatElement, hasLabelItem } = processedChildren;
    hasLabelRef.current = hasLabelItem;
    hadLabelRef.current = hadLabelRef.current || hasLabelRef.current;
    const classString = classNames(clsPrefix, {
        [`${clsPrefix}-pending`]: !!pending,
        [`${clsPrefix}-label`]: !!hasLabelItem,
        [`${clsPrefix}-${mode}`]: !!mode,
        [`${clsPrefix}-reverse`]: !!reverse
    }, className);

    // 使用useMemo缓存样式计算
    const styles = useMemo(() => {
        const wrapStyle: React.CSSProperties = {};
        const labelStyle: React.CSSProperties = {};

        if (hasLabelItem && typeof labelWidth === 'number') {
            if (mode === 'left' || mode === undefined) {
                wrapStyle.marginLeft = labelWidth;
                labelStyle.width = labelWidth;
            } else if (mode === 'right') {
                wrapStyle.marginRight = labelWidth;
                labelStyle.width = labelWidth;
            }
        }

        return { wrapStyle, labelStyle };
    }, [hasLabelItem, labelWidth, mode]);

    const { wrapStyle, labelStyle } = styles;

    let itemLen = flatElement.length; // item总的长度
    let itemIdx = 0; // 真实的索引位置（包括group组件child长度）
    const items = React.Children.map(timeLineItems, (ele, idx) =>{
        idx = itemIdx;
        let groupAttr = {}
        if (isGroup(ele)) { // 如果是group组件添加下列属性
            itemIdx += itemToArr(ele).length // group组件加上子元素长度
            groupAttr = {
                len: itemLen,
                timeClsPrefix: clsPrefix,
                reverse,
                pending,
                mode
            }
        } else {
            itemIdx += 1;
        }

        return React.cloneElement(ele, {
            last: !reverse && pending ? idx === itemLen - 2 : idx === itemLen - 1,
            timelineFieldId: props.fieldid,
            idx,
            className: classNames([
                ele?.props?.className,
                getPositionCls(ele, idx),
            ]),
            labelStyle,
            ...groupAttr
        })
    });
    let adapterNid = getNid(props);

    return (
        <ul {...others} dir={dir} className={classString} style={{...style, ...wrapStyle}} ref={timelineRef} {...adapterNid}>
            {items}
        </ul>
    );
});

// 添加静态属性
Timeline.displayName = 'Timeline';
(Timeline as any).Item = TimelineItem;
(Timeline as any).Group = TimelineItemGroup;

// Timeline.propTypes = propTypes;

export default WithConfigConsumer({name: "timeline"})(WebUI({name: "timeline", defaultProps})(Timeline));
